import type { FC } from 'react';
import React, { Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { cssMap, styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import SearchIcon from '@atlaskit/icon/core/search';
import { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { Inline } from '@atlaskit/primitives/compiled';

import { ContentDescription } from './ContentDescription';
import { SpaceDescription } from './SpaceDescription';
import {
	ConfluenceGroupUsageType,
	type AccessDetailsDialogContentAccessDetailsQuery as AccessDetailsDialogContentAccessDetailsQuery$data,
} from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type { AccessDetailsDialogSpaceAccessDetailsQuery as AccessDetailsDialogSpaceAccessDetailsQuery$data } from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';

type AccessDetailsDialogHeaderProps = {
	appliedFilterText: string;
	filterText: string;
	setFilterText: (_: string) => void;
	onClose: () => void;
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data;
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data;
	hasInheritedRestrictions: boolean;
	isLoading: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalHeaderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-testid="space-access-details.drilldown--header"]': {
		paddingBottom: token('space.200'),
	},
});

const styles = cssMap({
	header: {
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: token('space.300'),
	},
	headerLayout: {
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
	},
	title: { paddingBottom: token('space.200') },
	textField: { marginTop: token('space.200'), width: '500px' },
	searchIconWrapper: {
		padding: token('space.050'),
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const i18n = defineMessages({
	backButtonLabel: {
		id: 'space-access-details.access-details-dialog.back-button',
		defaultMessage: 'Back',
		description: 'Label for button that goes back to the previous screen',
	},
	clearFilterLabel: {
		id: 'space-access-details.access-details-dialog.clear-filter',
		defaultMessage: 'Clear selection',
		description: 'Label for button that clears text from the filter field',
	},
	countOfIndividualsSuffix: {
		id: 'space-access-details.access-details-dialog.count-of-groups-and-individuals-suffix',
		defaultMessage: 'and {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description:
			'Latter part of placeholder text when there are both individuals and groups, to be concatenated with space-access-details.access-details-dialog.count-of-groups',
	},
	countOfGroups: {
		id: 'space-access-details.access-details-dialog.count-of-groups',
		defaultMessage: 'Search {countOfGroups, plural, one {# group} other {# groups}}',
		description: 'Placeholder text when there are only groups',
	},
	countOfIndividuals: {
		id: 'space-access-details.access-details-dialog.count-of-individuals',
		defaultMessage: 'Search {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description: 'Placeholder text when there are only individuals',
	},
	countOfTeamsMiddleClause: {
		id: 'space-access-details.access-details-dialog.count-of-teams-middle-clause',
		defaultMessage: '{countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the space',
	},
	countOfTeams: {
		id: 'space-access-details.access-details-dialog.count-of-teams',
		defaultMessage: 'Search {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are only teams in the space',
	},
	countOfTeamsSuffix: {
		id: 'space-access-details.access-details-dialog.count-of-teams-suffix',
		defaultMessage: 'and {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the space',
	},
});

export const AccessDetailsDialogHeader: FC<AccessDetailsDialogHeaderProps> = ({
	hasInheritedRestrictions,
	onClose,
	filterText,
	setFilterText,
	spaceInfo,
	contentInfo,
	isLoading,
	appliedFilterText,
}) => {
	const intl = useIntl();
	const handleFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterText(event.currentTarget.value);
	};

	let countOfIndividuals;
	let countOfGroups;
	let countOfTeams;
	if (hasInheritedRestrictions) {
		countOfIndividuals =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.userCount;
		countOfTeams =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes?.filter(
				(subject) => subject?.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION,
			).length;
		const totalCount =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.groupCount;
		countOfGroups = totalCount && totalCount - countOfTeams;
	} else {
		countOfIndividuals = spaceInfo?.spacePermissions?.subjectsWithPermissions?.userCount || 0;
		countOfTeams = spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes?.filter(
			(subject) =>
				subject?.subjectKey?.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION,
		).length;
		const totalCount = spaceInfo?.spacePermissions?.subjectsWithPermissions?.groupCount;
		countOfGroups = totalCount && totalCount - countOfTeams;
	}

	let placeholder;
	// Input cleared but filter is still there - prevent flashing of placeholder
	if (isLoading || (appliedFilterText && !filterText)) {
		placeholder = '';
	} else {
		if (countOfIndividuals && countOfGroups && countOfTeams) {
			placeholder = `${intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			})}, ${intl.formatMessage(i18n.countOfTeamsMiddleClause, { countOfTeams })}, ${intl.formatMessage(
				i18n.countOfIndividualsSuffix,
				{
					countOfIndividuals,
				},
			)}`;
		} else if (countOfGroups && countOfIndividuals) {
			placeholder = `${intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
				countOfIndividuals,
			})}`;
		} else if (countOfIndividuals && countOfTeams) {
			placeholder = `${intl.formatMessage(i18n.countOfTeams, {
				countOfTeams,
			})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
				countOfIndividuals,
			})}`;
		} else if (countOfTeams && countOfGroups) {
			placeholder = `${intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			})} ${intl.formatMessage(i18n.countOfTeamsSuffix, {
				countOfTeams,
			})}`;
		} else if (countOfTeams) {
			placeholder = intl.formatMessage(i18n.countOfTeams, {
				countOfTeams,
			});
		} else if (countOfGroups) {
			placeholder = intl.formatMessage(i18n.countOfGroups, {
				countOfGroups,
			});
		} else {
			placeholder = intl.formatMessage(i18n.countOfIndividuals, {
				countOfIndividuals,
			});
		}
	}

	return (
		<ModalHeaderWrapper>
			<ModalHeader hasCloseButton>
				<Box css={styles.headerLayout}>
					<Box css={styles.title}>
						<Inline grow="fill" alignBlock="center" space="space.100">
							<Button
								aria-label={intl.formatMessage(i18n.backButtonLabel)}
								appearance="subtle"
								iconBefore={<ArrowLeftIcon label="" />}
								onClick={onClose}
							/>
							<ModalTitle>
								{hasInheritedRestrictions ? (
									<FormattedMessage
										id="space-access-details.access-details-dialog.restricting-page-header"
										defaultMessage="Who has access to the parent item"
										description="Dialog title for the case when page audience is limited by a parent page/content"
									/>
								) : (
									<FormattedMessage
										id="space-access-details.access-details-dialog.restricting-space-header"
										defaultMessage="Who has access to this space"
										description="Dialog title for the case when page audience is defined by space"
									/>
								)}
							</ModalTitle>
						</Inline>
					</Box>
					<Box css={styles.header}>
						{!hasInheritedRestrictions ? (
							<SpaceDescription spaceInfo={spaceInfo} isLoading={isLoading} />
						) : (
							<ContentDescription contentInfo={contentInfo} isLoading={isLoading} />
						)}
					</Box>
					<Textfield
						aria-label={placeholder}
						css={styles.textField}
						autoFocus
						placeholder={placeholder}
						value={filterText}
						onChange={handleFilterTextChange}
						elemBeforeInput={
							<Box css={styles.searchIconWrapper}>
								<SearchIcon spacing="spacious" color={token('color.icon.subtle')} label="" />
							</Box>
						}
						elemAfterInput={
							<Fragment>
								{Boolean(filterText) && (
									<Button
										appearance="subtle"
										aria-label={intl.formatMessage(i18n.clearFilterLabel)}
										iconBefore={<SelectClearIcon label="" color={token('color.icon.subtle')} />}
										onClick={() => setFilterText('')}
									/>
								)}
							</Fragment>
						}
					/>
				</Box>
			</ModalHeader>
		</ModalHeaderWrapper>
	);
};
